import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import PageTitle from '../../../components/Text/PageTitle';
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import ArrowLeft from '../../../components/Icons/ArrowLeft';
import { BannerFormData, BannerScope, BannerType } from '../../../models/BannersModels';
import { reset } from '../../../stores/BannersStore';
import { RootState } from '../../../stores/Store';
import BannersFormContainer from './components/BannersFormContainer';
import BannersFormReview from './components/BannersFormReview';
import useBannersForm from './useBannersForm';

function BannersForm() {
  const [t] = useTranslation();
  const { editMode, isLoading, overlappingBanners, publishBanner, saveDraft, resetOverlappingBanners } =
    useBannersForm();
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const formData = useSelector((state: RootState) => state.bannersReducer.form);
  const formTitle = editMode ? t('pages.bannerOnboarding.titleEdit') : t('pages.bannerOnboarding.title');

  useEffect(() => {
    // we never change screens so when switching between the form and the review steps we stay on the same scroll level
    // to simulate a screen change we scroll to the top of the page when changing steps
    window.scrollTo({ top: 0 });
  }, [step]);

  /**
   * Handle the close action
   */
  const handleClose = () => {
    navigate('/banners');
    dispatch(reset());
  };

  /**
   * Advance to the next step of the form
   */
  const nextStep = () => {
    setStep((prevState) => prevState + 1);
  };

  /**
   * Go back to the previous step of the form
   */
  const previousStep = () => {
    resetOverlappingBanners();
    setStep((prevState) => prevState - 1);
  };

  /**
   * Converts a picked date into a UTC ISO string
   * @param date
   * @param timezone
   */
  const toUtcISO = (date: string, timezone: string) => {
    if (!date) return undefined;
    const dateTime = DateTime.fromISO(date);
    const zonedDateTime = timezone ? dateTime.setZone(timezone, { keepLocalTime: true }) : dateTime;
    return zonedDateTime.toUTC().toISO() ?? undefined;
  };

  /**
   * Handle the publish action
   */
  const handlePublish = () => {
    publishBanner({
      bannerType: formData.bannerType as BannerType,
      scopeType: formData.scope || undefined,
      title: formData.bannerTitle.trim(),
      startAt: toUtcISO(formData.startDate, formData.timezone),
      endAt: toUtcISO(formData.endDate, formData.timezone),
      translations: formData.bannerType !== BannerType.OTHER ? {} : formData.messages,
      marketIds: formData.scope === BannerScope.MARKETS ? formData.selectedMarkets : [],
      companyIds: formData.scope === BannerScope.COMPANIES ? formData.selectedCompanies : [],
      dismissible: formData.dismissible,
      maintenanceWindow:
        formData.bannerType === BannerType.PLANNED_CHANGE && (formData.maintenanceStart || formData.maintenanceEnd)
          ? {
              startAt: toUtcISO(formData.maintenanceStart, formData.maintenanceTimezone),
              endAt: toUtcISO(formData.maintenanceEnd, formData.maintenanceTimezone),
            }
          : null,
      isDraft: false,
    });
  };

  /**
   * Handle the save as draft action
   * Uses the values currently in the form since they are only saved on the store when moving to the review step
   * @param values
   */
  const handleSaveDraft = (values: BannerFormData) => {
    saveDraft({
      bannerType: values.bannerType as BannerType,
      scopeType: values.scope || undefined,
      title: values.bannerTitle.trim(),
      startAt: toUtcISO(values.startDate, values.timezone),
      endAt: toUtcISO(values.endDate, values.timezone),
      translations: values.bannerType !== BannerType.OTHER ? {} : values.messages,
      marketIds: values.scope === BannerScope.MARKETS ? values.selectedMarkets : [],
      companyIds: values.scope === BannerScope.COMPANIES ? values.selectedCompanies : [],
      dismissible: values.dismissible,
      maintenanceWindow:
        values.bannerType === BannerType.PLANNED_CHANGE && (values.maintenanceStart || values.maintenanceEnd)
          ? {
              startAt: toUtcISO(values.maintenanceStart, values.maintenanceTimezone),
              endAt: toUtcISO(values.maintenanceEnd, values.maintenanceTimezone),
            }
          : null,
      isDraft: true,
    });
  };

  /**
   * Render the current step of the banner form
   */
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <BannersFormContainer
            editMode={editMode}
            isLoading={isLoading}
            onClose={handleClose}
            nextStep={nextStep}
            onSaveDraft={handleSaveDraft}
          />
        );
      case 2:
        return (
          <BannersFormReview
            editMode={editMode}
            onBack={previousStep}
            onClose={handleClose}
            onPublish={handlePublish}
            isLoading={isLoading}
            overlappingBanners={overlappingBanners}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Breadcrumbs
        activeLinks={[
          { label: t('navbar.banners'), url: '/banners' },
          ...(step === 2 ? [{ label: formTitle, url: location.pathname, onClick: previousStep }] : []),
        ]}
        disabledLinks={[step === 2 ? t('pages.bannerOnboarding.reviewAndPublish') : formTitle]}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', paddingBottom: '2.75rem' }}>
        <ArrowLeft
          stroke={theme.palette.primary.main}
          style={{ cursor: 'pointer', marginRight: '1rem' }}
          onClick={() => (step === 1 ? handleClose() : previousStep())}
        />
        <PageTitle>{step === 2 ? t('pages.bannerOnboarding.reviewAndPublish') : formTitle}</PageTitle>
      </Box>
      {renderStep()}
    </>
  );
}

export default BannersForm;
